import { Translation } from 'hooks/useTranslation/useTranslation';
import { FilterSection } from 'v2/components/SearchAndFilters/SearchAndFilters';
import { filterBatteryStatusDefault, filterDeviceTypeDefault } from './deviceFilterConstants';
import { DevicesFilterState, FilterDeviceGroupsState, FilterOfficeModeState } from './deviceFilterTypes';

interface Params {
  translation: Translation;
  filters: DevicesFilterState;
}

export const createDeviceFilterSections = ({ translation, filters }: Params): FilterSection[] => {
  const { filterDeviceGroups, filterDeviceType, filterBatteryStatus, filterOfficeMode } = filters.filter;
  const { setFilterDeviceGroups, setFilterDeviceType, setFilterBatteryStatus, setFilterOfficeMode } = filters.setters;

  return [
    {
      id: 'device_groups',
      header: translation.device_groups,
      type: 'radio',
      items: [
        {
          key: FilterDeviceGroupsState.Assigned,
          label: translation.assigned,
          checked: filterDeviceGroups === FilterDeviceGroupsState.Assigned,
          onChange: () => setFilterDeviceGroups(FilterDeviceGroupsState.Assigned)
        },
        {
          key: FilterDeviceGroupsState.NotAssigned,
          label: translation.not_assigned,
          checked: filterDeviceGroups === FilterDeviceGroupsState.NotAssigned,
          onChange: () => setFilterDeviceGroups(FilterDeviceGroupsState.NotAssigned)
        }
      ],
      isClearDisabled: filterDeviceGroups === null,
      onClear: () => setFilterDeviceGroups(null)
    },
    {
      id: 'device_type',
      header: translation.device_type,
      type: 'checkbox',
      items: [
        {
          key: 'reader',
          label: translation.reader,
          checked: filterDeviceType.reader,
          onChange: () => setFilterDeviceType((current) => ({ ...current, reader: !current.reader }))
        },
        {
          key: 'controller',
          label: translation.controller,
          checked: filterDeviceType.controller,
          onChange: () => setFilterDeviceType((current) => ({ ...current, controller: !current.controller }))
        },
        {
          key: 'handle',
          label: translation.handle,
          checked: filterDeviceType.handle,
          onChange: () => setFilterDeviceType((current) => ({ ...current, handle: !current.handle }))
        },
        {
          key: 'swingHandle',
          label: translation.swing_handle,
          checked: filterDeviceType.swingHandle,
          onChange: () => setFilterDeviceType((current) => ({ ...current, swingHandle: !current.swingHandle }))
        },
        {
          key: 'cylinder',
          label: translation.cylinder,
          checked: filterDeviceType.cylinder,
          onChange: () => setFilterDeviceType((current) => ({ ...current, cylinder: !current.cylinder }))
        },
        {
          key: 'locker',
          label: translation.locker,
          checked: filterDeviceType.locker,
          onChange: () => setFilterDeviceType((current) => ({ ...current, locker: !current.locker }))
        }
      ],
      isClearDisabled: !Object.values(filterDeviceType).some(Boolean),
      onClear: () => setFilterDeviceType(filterDeviceTypeDefault)
    },
    {
      id: 'battery_status',
      header: translation.battery_status,
      type: 'checkbox',
      items: [
        {
          key: 'unknown',
          label: translation.unknown,
          checked: filterBatteryStatus.unknown,
          onChange: () => setFilterBatteryStatus((current) => ({ ...current, unknown: !current.unknown }))
        },
        {
          key: 'empty',
          label: translation.empty,
          checked: filterBatteryStatus.empty,
          onChange: () => setFilterBatteryStatus((current) => ({ ...current, empty: !current.empty }))
        },
        {
          key: 'critical',
          label: translation.critical,
          checked: filterBatteryStatus.critical,
          onChange: () => setFilterBatteryStatus((current) => ({ ...current, critical: !current.critical }))
        },
        {
          key: 'low',
          label: translation.low,
          checked: filterBatteryStatus.low,
          onChange: () => setFilterBatteryStatus((current) => ({ ...current, low: !current.low }))
        },
        {
          key: 'full',
          label: translation.full,
          checked: filterBatteryStatus.full,
          onChange: () => setFilterBatteryStatus((current) => ({ ...current, full: !current.full }))
        }
      ],
      isClearDisabled: !Object.values(filterBatteryStatus).some(Boolean),
      onClear: () => setFilterBatteryStatus(filterBatteryStatusDefault)
    },
    {
      id: 'office_mode',
      header: translation.office_mode,
      type: 'radio',
      items: [
        {
          key: FilterOfficeModeState.HasOfficeMode,
          label: translation.has_office_mode,
          checked: filterOfficeMode === FilterOfficeModeState.HasOfficeMode,
          onChange: () => setFilterOfficeMode(FilterOfficeModeState.HasOfficeMode)
        },
        {
          key: FilterOfficeModeState.NoOfficeMode,
          label: translation.no_office_mode,
          checked: filterOfficeMode === FilterOfficeModeState.NoOfficeMode,
          onChange: () => setFilterOfficeMode(FilterOfficeModeState.NoOfficeMode)
        }
      ],
      isClearDisabled: filterOfficeMode === null,
      onClear: () => setFilterOfficeMode(null)
    }
  ];
};
